import { Button, Icon, Modal, Typography } from '@smartgift/frontend-toolkit';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { COLLECTIONS_TAB, MY_COLLECTIONS_TABLE_LIMIT, QUERY_KEYS } from '@hero/constants';
import { Table, OptionsDropdown } from '@hero/molecules';
import { ChooseFeatureTypeModal, TableProducts } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { productServices } from '@hero/services';
import {
  useDeleteCollection,
  useDuplicateCollection,
  useGetCollections
} from '@hero/services/product-service/collection/hooks';
import { useNotification } from '@hero/store/hooks';
import { createQueryKey, getNameFeatureFormData } from '@hero/utils';
import { getNameSchema } from '@hero/validations';

import * as S from './style';

export function CollectionTypeTable({ activeTab = COLLECTIONS_TAB.saved, createNew }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showNotification, showSuccess } = useNotification();
  const { t } = useTranslation();
  const { mutateAsync: deleteCollection } = useDeleteCollection();
  const { mutateAsync: duplicateCollection } = useDuplicateCollection();

  const [globalFilters, setGlobalFilters] = useState({});
  const [showWhatIsCollectionModal, setShowWhatIsCollectionModal] = useState(false);
  const [chooseFeatureModal, setChooseFeatureModal] = useState(false);
  const [initialProducts, setInitialProducts] = useState([]);

  const [{ pageIndex, pageSize }, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: MY_COLLECTIONS_TABLE_LIMIT
  });

  const { data: myCollections, isLoading: isFetchingMyCollections } = useGetCollections({
    filters: {
      isPublic: activeTab === COLLECTIONS_TAB.featured,
      name: globalFilters?.searchTerm
    },
    pagination: {
      page: pageIndex + 1,
      limit: MY_COLLECTIONS_TABLE_LIMIT
    }
  });

  const renderProducts = ({ row }) => {
    return <TableProducts products={row.original?.products || []} />;
  };

  const onDeleteModal = row => {
    showNotification({
      formStyle: {
        padding: '0 50px'
      },
      primaryButtonProps: { color: 'error', children: t('delete') },
      secondaryButtonProps: { children: t('keepCollection') },
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      title: t('areYouSureYouWantDelete', { type: t('collection').toLocaleLowerCase() }),
      onSubmit: async () => {
        try {
          await deleteCollection(row.original.id);
        } finally {
          showSuccess({
            type: 'toast',
            message: t('successfullyRemovedMessage', { name: row.original.name })
          });
        }
      }
    });
  };

  const onDuplicateModal = row => {
    showNotification({
      header: t('nameYourFeature', { name: t('collection').toLocaleLowerCase() }),
      notificationImage: <Icon name="collections" size="xxl" />,
      formData: getNameFeatureFormData({
        label: t('featureName', { featureType: t('collection') }),
        value: t('copyText', { copyText: row.original.name })
      }),
      formValidationSchema: getNameSchema(),
      onSubmit: async ({ formValues }) => {
        const response = await duplicateCollection({
          ...formValues,
          products: row?.original?.products
        });

        navigate(ROUTE_PATHS.COLLECTIONS_DETAIL(response.id));
      },
      primaryButtonProps: {
        endIcon: <Icon name="next" />,
        children: t('createAndReview')
      },
      secondaryButtonProps: { children: t('cancel') }
    });
  };

  const handleSendWithFeature = async row => {
    const products = await queryClient.fetchQuery(
      createQueryKey(QUERY_KEYS.useGetProductsByCodesKey, {
        filters: { productCodes: row.original.products.map(p => p.merchantProductId) }
      }),
      () =>
        productServices.searchProducts({
          filters: {
            productCodes: row.original.products.map(p => p.merchantProductId)
          }
        })
    );

    const initialProducts = products.docs.map(doc => {
      const p = row.original.products.find(p => p.merchantProductId === doc.merchantProductId);
      doc.skus = doc.skus.filter(sku => p.skus.find(s => s.code === sku.code));

      return doc;
    });
    setInitialProducts(initialProducts);
    setChooseFeatureModal(true);
  };

  const onNavigateToDetailPage = row => {
    navigate(ROUTE_PATHS.COLLECTIONS_DETAIL(row.original.id));
  };

  const columns = React.useMemo(
    () => [
      {
        header: t('name'),
        accessorKey: 'name',
        cell: ({ row }) => (
          <Button
            variant="link"
            color="neutral"
            emphasis="800"
            onClick={() => onNavigateToDetailPage(row)}
          >
            {row.original.name}
          </Button>
        )
      },
      { Header: t('gifts'), accessorKey: 'products', cell: renderProducts },
      {
        id: 'actions',
        header: '',
        size: 'auto',
        cell: ({ row }) => {
          const options = [
            {
              label: t('seeDetails'),
              onClick: () => {
                onNavigateToDetailPage(row);
              }
            },
            {
              label: t('duplicate'),
              onClick: () => {
                onDuplicateModal(row);
              }
            }
          ];

          if (row.original.products.length > 0) {
            options.unshift({
              label: t('quickSend'),
              onClick: () => {
                handleSendWithFeature(row);
              }
            });
          }

          if (activeTab === COLLECTIONS_TAB.saved) {
            options.push({
              label: t('delete'),
              onClick: () => {
                onDeleteModal(row);
              }
            });
          }

          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <OptionsDropdown
                popperOptions={{
                  placement: 'left',
                  modifiers: [
                    {
                      name: 'offset',
                      enabled: true,
                      options: {
                        offset: [50, 0]
                      }
                    }
                  ]
                }}
                options={options}
              >
                <Icon name="3dot" px={18} />
              </OptionsDropdown>
            </div>
          );
        }
      }
    ],
    []
  );

  return (
    <S.CollectionTypeTable>
      <S.CollectionTypeTableHeader>
        <Typography variant="body2" color="neutral" emphasis="800">
          <b>{myCollections?.pagination?.total}</b> {t('totalCollections')}
        </Typography>

        <Button
          startIcon={<Icon name="info" color="neutral" />}
          variant="link"
          color="neutral"
          emphasis="800"
          onClick={() => {
            setShowWhatIsCollectionModal(true);
          }}
        >
          {t('whatIsCollection')}
        </Button>
        <Modal
          isOpen={showWhatIsCollectionModal}
          onRequestClose={() => setShowWhatIsCollectionModal(false)}
          size="sm"
          title={t('whatIsCollection')}
          primaryButtonProps={{
            onClick: () => setShowWhatIsCollectionModal(false)
          }}
        >
          <Typography variant="body2">{t('whatIsCollectionSubTitle')}</Typography>
          <S.CollectionInfoModalList>
            <li>
              <Typography variant="body2">{t('whatIsCollectionItem1')}</Typography>
            </li>
            <li>
              <Typography variant="body2">{t('whatIsCollectionItem2')}</Typography>
            </li>
            <li>
              <Typography variant="body2">{t('whatIsCollectionItem3')}</Typography>
            </li>
          </S.CollectionInfoModalList>
        </Modal>
      </S.CollectionTypeTableHeader>
      <Table
        pageCount={myCollections?.pagination?.pages}
        columns={columns}
        data={myCollections?.docs || []}
        globalFilters={globalFilters}
        pagination={{
          pageIndex,
          pageSize,
          total: myCollections?.pagination?.total
        }}
        loading={isFetchingMyCollections}
        setGlobalFilters={setGlobalFilters}
        setPagination={setPagination}
        manualPagination
        enabled={{ filter: false, search: true, header: true }}
        emptyTableContent={{
          feature: 'collections',
          icon: 'collections',
          createNew
        }}
      />

      <ChooseFeatureTypeModal
        key={initialProducts}
        redirectToGiftStep
        initialProducts={initialProducts}
        setInitialProducts={setInitialProducts}
        isOpen={chooseFeatureModal}
        onClose={() => {
          setChooseFeatureModal(false);
        }}
      />
    </S.CollectionTypeTable>
  );
}
